/**
 * PromoteChatDialog Component
 * Modal for promoting chat conversations to test scenarios
 * Matches the original Alpine.js implementation (chat_id, name, description)
 */

import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Plus } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { PromoteChatRequest } from '@/types/evalScenarios';

interface PromoteChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PromoteChatRequest) => Promise<void>;
  isSubmitting?: boolean;
  categories: string[];
  initialTopicId?: string;
  initialTopicTitle?: string;
  initialAgentType?: string;
}

export const PromoteChatDialog: React.FC<PromoteChatDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  categories,
  initialTopicId,
  initialTopicTitle,
  initialAgentType,
}) => {
  const { messages } = useI18n();
  const t = messages.evalScenarios;
  const tc = messages.common;
  const [formData, setFormData] = useState<PromoteChatRequest>({
    chat_id: initialTopicId || '',
    agent_type: initialAgentType || 'dora',
    name: initialTopicTitle ? `Test: ${initialTopicTitle}` : '',
    description: '',
    category: '',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');

  // Update form when initial values change
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        chat_id: initialTopicId || prev.chat_id || '',
        agent_type: initialAgentType || prev.agent_type || 'dora',
        name: initialTopicTitle ? `Test: ${initialTopicTitle}` : prev.name || '',
      }));
    }
  }, [isOpen, initialTopicId, initialTopicTitle, initialAgentType]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[PromoteChatDialog] Submitting:', formData);
    try {
      await onSubmit({
        ...formData,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
      });
      console.log('[PromoteChatDialog] Submit successful');
    } catch (error) {
      console.error('[PromoteChatDialog] Submit failed:', error);
      throw error;
    }
  };

  const handleClose = () => {
    setFormData({
      chat_id: '',
      agent_type: 'dora',
      name: '',
      description: '',
      category: '',
      tags: [],
    });
    setTagInput('');
    onClose();
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tag],
      });
    }
    setTagInput('');
  };

  const removeTag = (index: number) => {
    setFormData({
      ...formData,
      tags: (formData.tags || []).filter((_, i) => i !== index),
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop - no onClick to prevent accidental close */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t.promoteChatTitle}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-900">
              {t.promoteChatDescription}
            </p>
          </div>

          {/* Chat ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.chatTopicId} <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.chat_id}
              onChange={(e) => setFormData({ ...formData, chat_id: e.target.value })}
              placeholder={t.chatTopicIdPlaceholder}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              {t.chatTopicIdHint}
            </p>
          </div>

          {/* Scenario Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.scenarioName} <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t.scenarioNamePlaceholder}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.description}
            </label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t.descriptionPlaceholder}
              rows={2}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.category}
            </label>
            <Input
              value={formData.category || ''}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder={t.categoryPlaceholder}
              list="promote-category-list"
            />
            <datalist id="promote-category-list">
              {categories.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.tags}</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder={t.addTagPlaceholder}
                className="flex-1"
              />
              <Button type="button" variant="secondary" onClick={addTag}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(formData.tags || []).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm flex items-center gap-1"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    className="hover:text-purple-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="secondary" onClick={handleClose}>
              {tc.cancel}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={!formData.chat_id || !formData.name}
            >
              {t.promoteButton}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
